import { useContext, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Container,
  Skeleton,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import { GlassButtonLoading } from "../../themes/UiElements";
import { UserContext } from "../../context/UserProvider";
import BackBtn from "../../widgets/BackBtn";

export default function SheetSchemaList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: userData, loading, reloadUser } = useContext(UserContext);

  const { data, isLoading, isError } = useApiEp("/user/sheet-schemas");
  const schemas = data?.data ?? [];

  // Active schema is identified by sheetName stored in userData
  const activeSheetName = userData?.googleSheet;

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [actionLoading, setActionLoading] = useState(null);

  const invalidate = () =>
    queryClient.invalidateQueries([`${API_ENDPOINT}/user/sheet-schemas`]);

  const handleSetActive = async (schema) => {
    setActionLoading(`active-${schema._id}`);
    try {
      await axios.post(
        `${API_ENDPOINT}/user/set-active-sheet-schema/${schema.sheetName}`,
        {},
        getHeader()
      );
      await reloadUser(); // refresh userData so activeSheetName updates
      setSnackbar({
        open: true,
        msg: `"${schema.sheetName}" is now active`,
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        msg: "Failed to set active schema",
        severity: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    const schema = deleteTarget;
    setDeleteTarget(null);
    setActionLoading(`delete-${schema._id}`);
    try {
      await axios.delete(
        `${API_ENDPOINT}/user/sheet-schemas/${schema._id}`,
        getHeader()
      );
      await invalidate();
      setSnackbar({
        open: true,
        msg: `"${schema.sheetName}" deleted`,
        severity: "success",
      });
    } catch (err) {
      const msg = err?.response?.data?.message ?? "Failed to delete schema";
      setSnackbar({ open: true, msg, severity: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        backgroundImage:
          "radial-gradient(ellipse at 20% 0%, rgba(74,222,128,0.06) 0%, transparent 60%)",
        p: { xs: 2, md: 4 },
      }}
    >
      <Container>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
          }}
        >
          <BackBtn />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "rgba(74,222,128,0.12)",
                border: "1px solid rgba(74,222,128,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TableChartIcon sx={{ color: "primary.main", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{ color: "primary.main", letterSpacing: "-0.5px" }}
              >
                Sheet Schemas
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Manage your Google Sheet column structures
              </Typography>
            </Box>
          </Box>

          <GlassButtonLoading
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/home/sheets-schema-design")}
          >
            New Schema
          </GlassButtonLoading>
        </Box>

        {/* Loading skeletons */}
        {isLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={96}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        )}

        {/* Error */}
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load schemas. Please refresh.
          </Alert>
        )}

        {/* Empty state */}
        {!isLoading && !isError && schemas.length === 0 && (
          <Paper
            sx={{
              p: 6,
              textAlign: "center",
              border: "1px dashed rgba(74,222,128,0.25)",
              bgcolor: "rgba(74,222,128,0.02)",
            }}
          >
            <TableChartIcon
              sx={{ fontSize: 48, color: "rgba(74,222,128,0.3)", mb: 2 }}
            />
            <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
              No schemas yet
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              Create your first sheet schema to get started.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate("/home/sheets-schema-design")}
              sx={{
                borderColor: "rgba(74,222,128,0.4)",
                color: "primary.main",
              }}
            >
              Create Schema
            </Button>
          </Paper>
        )}

        {/* Schema cards */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {schemas.map((schema) => {
            const isActive = schema.sheetName === activeSheetName; // ← derived here
            return (
              <Paper
                key={schema._id}
                sx={{
                  p: 3,
                  border: "1px solid",
                  borderColor: isActive
                    ? "rgba(74,222,128,0.45)"
                    : "rgba(74,222,128,0.1)",
                  bgcolor: isActive
                    ? "rgba(74,222,128,0.04)"
                    : "background.paper",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: "rgba(74,222,128,0.35)" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  {/* Left: info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "text.primary",
                          fontWeight: 600,
                          fontSize: "1rem",
                          letterSpacing: "-0.3px",
                        }}
                      >
                        {schema.sheetName}
                      </Typography>
                      {isActive && (
                        <Chip
                          label="Active"
                          size="small"
                          icon={
                            <CheckCircleIcon
                              sx={{ fontSize: "13px !important" }}
                            />
                          }
                          sx={{
                            bgcolor: "rgba(74,222,128,0.15)",
                            color: "primary.main",
                            border: "1px solid rgba(74,222,128,0.3)",
                            fontSize: 11,
                            height: 22,
                          }}
                        />
                      )}
                    </Box>

                    {/* Column chips */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {schema.columns.map((col, i) => (
                        <Chip
                          key={i}
                          size="small"
                          icon={
                            ["phoneNumber", "message"].includes(col.name) ? (
                              <LockIcon sx={{ fontSize: "11px !important" }} />
                            ) : undefined
                          }
                          label={
                            <span>
                              <span
                                style={{
                                  fontFamily: "'DM Mono', monospace",
                                  fontSize: 11,
                                }}
                              >
                                {col.name}
                              </span>
                              <span
                                style={{
                                  opacity: 0.5,
                                  marginLeft: 3,
                                  fontSize: 10,
                                }}
                              >
                                {col.type}
                              </span>
                              {col.required && (
                                <span
                                  style={{ color: "#4ade80", marginLeft: 2 }}
                                >
                                  *
                                </span>
                              )}
                            </span>
                          }
                          sx={{
                            bgcolor: "rgba(74,222,128,0.06)",
                            border: "1px solid rgba(74,222,128,0.18)",
                            color: "text.secondary",
                            height: 22,
                          }}
                        />
                      ))}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{ color: "text.disabled", mt: 1, display: "block" }}
                    >
                      {schema.columns.length} columns · Updated{" "}
                      {new Date(schema.updatedOn).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {/* Right: actions */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    <Tooltip
                      title={
                        isActive ? "Already active" : "Set as active schema"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={
                            isActive || actionLoading === `active-${schema._id}`
                          }
                          onClick={() => handleSetActive(schema)}
                          sx={{
                            color: isActive
                              ? "primary.main"
                              : "rgba(74,222,128,0.5)",
                            "&:hover:not(:disabled)": {
                              color: "primary.main",
                              bgcolor: "rgba(74,222,128,0.08)",
                            },
                          }}
                        >
                          {isActive ? (
                            <CheckCircleIcon fontSize="small" />
                          ) : (
                            <RadioButtonUncheckedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Edit schema">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/home/sheet-schemas/${schema._id}/edit`)
                        }
                        sx={{
                          color: "rgba(74,222,128,0.6)",
                          "&:hover": {
                            color: "primary.main",
                            bgcolor: "rgba(74,222,128,0.08)",
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={"Delete schema"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={actionLoading === `delete-${schema._id}`}
                          onClick={() => setDeleteTarget(schema)}
                          sx={{
                            color: "rgba(74,222,128,0.5)",
                            "&:hover:not(:disabled)": {
                              color: "#f87171",
                              bgcolor: "rgba(248,113,113,0.08)",
                            },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Container>

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            border: "1px solid rgba(248,113,113,0.25)",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: "#f87171" }}>Delete Schema</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.secondary" }}>
            Are you sure you want to delete{" "}
            <strong style={{ color: "white" }}>
              {deleteTarget?.sheetName}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            sx={{
              bgcolor: "rgba(248,113,113,0.15)",
              color: "#f87171",
              border: "1px solid rgba(248,113,113,0.3)",
              "&:hover": { bgcolor: "rgba(248,113,113,0.25)" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ fontFamily: "'DM Mono', monospace" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
